import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { usePublicClient, useWalletClient, useAccount } from 'wagmi';
import { Abi } from 'viem';
import { CONFIG } from '../config';
import { processUIPageData, isIpfsCid, processImageData } from '../utils/ipfsUtils';

interface LoadingState {
  isLoading: boolean;
  step: string;
  progress: number;
}

export default function ProductPage() {
  const { address } = useParams<{ address: string }>();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { address: userAddress } = useAccount();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [pageCIDs, setPageCIDs] = useState<{ [index: number]: string }>({});
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<LoadingState>({ isLoading: false, step: '', progress: 0 });
  const [error, setError] = useState<string | null>(null);
  const [propertiesAddress, setPropertiesAddress] = useState<`0x${string}` | null>(null);
  const [productType, setProductType] = useState<string>('');
  const [productImage, setProductImage] = useState<string | null>(null);
  const [productName, setProductName] = useState<string>('');
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [imageUrlIndex, setImageUrlIndex] = useState<number>(0);
  const [pendingWalletRequest, setPendingWalletRequest] = useState<any>(null);

  // Handle wallet connection requests from iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Only handle messages from our iframe
      if (event.source !== iframeRef.current?.contentWindow) return;

      console.log('ProductPage: Received message from iframe:', event.data);

      switch (event.data.type) {
        case 'WALLET_CONNECT_REQUEST':
          try {
            if (!walletClient || !userAddress) {
              // Store the request and trigger wallet connection
              setPendingWalletRequest(event.data);
              
              // Use the global RainbowKit connect modal function
              if (window.openRainbowKitConnectModal) {
                window.openRainbowKitConnectModal();
              } else {
                // Fallback: try to find any button that might be the connect button
                const buttons = document.querySelectorAll('button');
                const connectBtn = Array.from(buttons).find(btn => 
                  btn.textContent?.includes('Connect') || 
                  btn.title?.includes('Connect') ||
                  btn.getAttribute('aria-label')?.includes('Connect')
                );
                if (connectBtn) {
                  connectBtn.click();
                } else {
                  // If we can't find the connect button, send an error response
                  iframeRef.current?.contentWindow?.postMessage({
                    type: 'WALLET_CONNECT_RESPONSE',
                    success: false,
                    error: 'Please connect your wallet using the connect button in the header'
                  }, '*');
                }
              }
              return;
            }

            // Use the connected account address
            iframeRef.current?.contentWindow?.postMessage({
              type: 'WALLET_CONNECT_RESPONSE',
              success: true,
              account: userAddress
            }, '*');
          } catch (error) {
            console.error('ProductPage: Wallet connection error:', error);
            iframeRef.current?.contentWindow?.postMessage({
              type: 'WALLET_CONNECT_RESPONSE',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            }, '*');
          }
          break;

        case 'WALLET_GET_ACCOUNTS_REQUEST':
          try {
            if (!walletClient || !userAddress) {
              iframeRef.current?.contentWindow?.postMessage({
                type: 'WALLET_GET_ACCOUNTS_RESPONSE',
                success: false,
                error: 'Wallet not connected in parent window'
              }, '*');
              return;
            }

            // Return the connected account
            iframeRef.current?.contentWindow?.postMessage({
              type: 'WALLET_GET_ACCOUNTS_RESPONSE',
              success: true,
              accounts: [userAddress]
            }, '*');
          } catch (error) {
            console.error('ProductPage: Get accounts error:', error);
            iframeRef.current?.contentWindow?.postMessage({
              type: 'WALLET_GET_ACCOUNTS_RESPONSE',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            }, '*');
          }
          break;

        case 'WALLET_SEND_TRANSACTION_REQUEST':
          try {
            if (!walletClient) {
              iframeRef.current?.contentWindow?.postMessage({
                type: 'WALLET_SEND_TRANSACTION_RESPONSE',
                success: false,
                error: 'Wallet not connected in parent window'
              }, '*');
              return;
            }

            const { to, data, value } = event.data.transaction;
            const hash = await walletClient.sendTransaction({
              to,
              data,
              value: value || 0n
            });

            iframeRef.current?.contentWindow?.postMessage({
              type: 'WALLET_SEND_TRANSACTION_RESPONSE',
              success: true,
              hash: hash
            }, '*');
          } catch (error) {
            console.error('ProductPage: Send transaction error:', error);
            iframeRef.current?.contentWindow?.postMessage({
              type: 'WALLET_SEND_TRANSACTION_RESPONSE',
              success: false,
              error: error instanceof Error ? error.message : 'Unknown error'
            }, '*');
          }
          break;

        case 'PING':
          // Respond to ping messages
          iframeRef.current?.contentWindow?.postMessage({ type: 'PONG' }, '*');
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [walletClient, userAddress]);

  // Handle wallet connection success
  useEffect(() => {
    if (walletClient && userAddress && pendingWalletRequest) {
      // Wallet is now connected, handle the pending request
      const handlePendingRequest = async () => {
        try {
          // Use the connected account address
          iframeRef.current?.contentWindow?.postMessage({
            type: 'WALLET_CONNECT_RESPONSE',
            success: true,
            account: userAddress
          }, '*');
        } catch (error) {
          console.error('ProductPage: Pending wallet request error:', error);
          iframeRef.current?.contentWindow?.postMessage({
            type: 'WALLET_CONNECT_RESPONSE',
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
          }, '*');
        }
        setPendingWalletRequest(null);
      };

      handlePendingRequest();
    }
  }, [walletClient, userAddress, pendingWalletRequest]);

  // Helper function to convert image data to proper src format (same as ProductCard)
  const getImageSrc = (imageData: string) => {
    const processedUrl = processImageData(imageData);
    console.log('ProductPage: getImageSrc - Input:', imageData, 'Output:', processedUrl);
    return processedUrl;
  };

  // Helper function to get fallback image URLs
  const getFallbackImageUrls = (imageData: string) => {
    if (!imageData || !imageData.includes('|')) return [];
    
    const [cid, filename] = imageData.split('|');
    if (!cid || !filename) return [];
    
    return [
      `https://ipfs.io/ipfs/${cid}/${filename}`,
      `https://w3s.link/ipfs/${cid}/${filename}`,
      `https://gateway.pinata.cloud/ipfs/${cid}/${filename}`,
      `https://cloudflare-ipfs.com/ipfs/${cid}/${filename}`
    ];
  };

  // Handle image loading error with fallback
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.log('ProductPage: Failed to load product image:', productImage);
    console.log('ProductPage: Image URL that failed:', e.currentTarget.src);
    
    // Try next gateway if available
    const fallbackUrls = getFallbackImageUrls(productImage || '');
    if (fallbackUrls.length > imageUrlIndex + 1) {
      const nextIndex = imageUrlIndex + 1;
      const nextUrl = fallbackUrls[nextIndex];
      console.log('ProductPage: Trying next gateway:', nextUrl);
      setCurrentImageUrl(nextUrl);
      setImageUrlIndex(nextIndex);
    } else {
      console.log('ProductPage: No more gateways to try, hiding image');
      e.currentTarget.style.display = 'none';
    }
  };

  const imageLoadedRef = useRef(false);

  // Monitor productImage changes for debugging
  useEffect(() => {
    console.log('ProductPage: productImage state changed:', productImage, 'type:', typeof productImage);
  }, [productImage]);

  // Monitor productName changes for debugging
  useEffect(() => {
    console.log('ProductPage: productName state changed:', productName, 'type:', typeof productName);
  }, [productName]);

  // Monitor productType changes for debugging
  useEffect(() => {
    console.log('ProductPage: productType state changed:', productType, 'type:', typeof productType);
  }, [productType]);

  // Handle image URL generation and fallback logic
  useEffect(() => {
    if (productImage && typeof productImage === 'string' && productImage.trim() !== '') {
      const fallbackUrls = getFallbackImageUrls(productImage);
      if (fallbackUrls.length > 0) {
        setCurrentImageUrl(fallbackUrls[0]);
        setImageUrlIndex(0);
        console.log('ProductPage: Setting initial image URL:', fallbackUrls[0]);
      } else {
        const processedUrl = getImageSrc(productImage);
        setCurrentImageUrl(processedUrl);
        console.log('ProductPage: Setting processed image URL:', processedUrl);
      }
    }
  }, [productImage]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // On mount, get the properties contract address and product type
  useEffect(() => {
    if (!address || !publicClient) return;
    
    // Validate the address format
    if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
      setError('Invalid product address format');
      setLoading({ isLoading: false, step: '', progress: 0 });
      return;
    }
    
    setLoading({ isLoading: true, step: 'Loading product properties...', progress: 25 });
    setError(null);
    (async () => {
      try {
        // First, check if the product contract exists by calling a simple function
        try {
          await publicClient.readContract({
            address: address as `0x${string}`,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
            functionName: 'owner',
          });
        } catch (error) {
          setError('Product contract not found at this address. Please check the address and try again.');
          setLoading({ isLoading: false, step: '', progress: 0 });
          return;
        }
        
        // Get product type (className) from contract
        try {
          const className = await publicClient.readContract({
            address: address as `0x${string}`,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
            functionName: 'className',
          }) as string;
          setProductType(className);
          console.log('ProductPage: Product type loaded:', className);
        } catch (error) {
          console.warn('ProductPage: Failed to get product type, using default');
          setProductType('TrinityProduct');
        }
        
        // Get product name from contract
        try {
          const name = await publicClient.readContract({
            address: address as `0x${string}`,
            abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
            functionName: 'name',
          }) as string;
          setProductName(name);
          console.log('ProductPage: Product name loaded:', name);
        } catch (error) {
          console.warn('ProductPage: Failed to get product name');
        }
        
        // Get properties contract address from product contract
        const propsAddr = await publicClient.readContract({
          address: address as `0x${string}`,
          abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
          functionName: 'properties',
        }) as `0x${string}`;
        setPropertiesAddress(propsAddr);
        setLoading({ isLoading: true, step: 'Properties loaded, fetching page data...', progress: 50 });
      } catch (e: any) {
        setError('Failed to load product properties: ' + (e.message || e.toString()));
        setLoading({ isLoading: false, step: '', progress: 0 });
      }
    })();
  }, [address, publicClient]);

  // When propertiesAddress is set, fetch page 0, page count, and product image
  useEffect(() => {
    if (!propertiesAddress || !publicClient || !address || imageLoadedRef.current) return;
    imageLoadedRef.current = true;
    setLoading({ isLoading: true, step: 'Loading page data...', progress: 75 });
    setError(null);
    (async () => {
      try {
        // Get page 0 CID
        const cid0 = await publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'getUIPage',
          args: [0]
        }) as string;
        
        // Get page count
        const pageCountResult = await publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'getUIPageCount',
        }) as bigint;
        
        // Get product image
        const imageResult = await publicClient.readContract({
          address: propertiesAddress,
          abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
          functionName: 'getUIImage',
          args: [0]
        }) as string;
        
        console.log('ProductPage: Page data loaded:', { cid0, pageCountResult, imageResult });
        
        // Set page count
        const pageCountNum = Number(pageCountResult);
        setPageCount(pageCountNum);
        console.log('ProductPage: Page count set to:', pageCountNum);
        
        // Set page CIDs
        if (cid0 && isIpfsCid(cid0)) {
          setPageCIDs(prev => ({ ...prev, 0: cid0 }));
          console.log('ProductPage: Page 0 CID set to:', cid0);
        }
        
        // Set product image
        if (imageResult && imageResult.trim() !== '') {
          setProductImage(imageResult);
          console.log('ProductPage: Product image set to:', imageResult);
        }
        
        // Load the first page
        if (cid0 && isIpfsCid(cid0)) {
          const ipfsUrl = processUIPageData(cid0);
          console.log('ProductPage: Loading first page from:', ipfsUrl);
          setIframeSrc(ipfsUrl);
          setLoading({ isLoading: true, step: 'Loading IPFS content...', progress: 95 });
          
          // Set a timeout to complete loading after 10 seconds
          loadingTimeoutRef.current = setTimeout(() => {
            console.log('ProductPage: Loading timeout reached, completing load');
            setLoading({ isLoading: false, step: '', progress: 100 });
          }, 10000);
        } else {
          console.log('ProductPage: No valid page CID found');
          setLoading({ isLoading: false, step: '', progress: 100 });
        }
      } catch (e: any) {
        console.error('ProductPage: Failed to load page data:', e);
        setError('Failed to load page data: ' + (e.message || e.toString()));
        setLoading({ isLoading: false, step: '', progress: 0 });
      }
    })();
  }, [propertiesAddress, publicClient, address]);

  // Handle page click
  const handlePageClick = async (idx: number) => {
    if (loading.isLoading || idx === currentPageIndex) return;
    
    console.log('ProductPage: Loading page', idx);
    setCurrentPageIndex(idx);
    setLoading({ isLoading: true, step: 'Loading page...', progress: 95 });
    setError(null);
    
    // Check if we already have the CID for this page
    if (pageCIDs[idx]) {
      const ipfsUrl = processUIPageData(pageCIDs[idx]);
      console.log('ProductPage: Loading cached page from:', ipfsUrl);
      setIframeSrc(ipfsUrl);
      setLoading({ isLoading: false, step: '', progress: 100 });
      return;
    }
    
    // Fetch the page CID from the contract
    try {
      const cid = await publicClient.readContract({
        address: propertiesAddress!,
        abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
        functionName: 'getUIPage',
        args: [idx]
      }) as string;
      
      if (cid && isIpfsCid(cid)) {
        setPageCIDs(prev => ({ ...prev, [idx]: cid }));
        const ipfsUrl = processUIPageData(cid);
        console.log('ProductPage: Loading new page from:', ipfsUrl);
        setIframeSrc(ipfsUrl);
        setLoading({ isLoading: false, step: '', progress: 100 });
      } else {
        setError('Invalid page data');
        setLoading({ isLoading: false, step: '', progress: 0 });
      }
    } catch (e: any) {
      console.error('ProductPage: Failed to load page:', e);
      setError('Failed to load page: ' + (e.message || e.toString()));
      setLoading({ isLoading: false, step: '', progress: 0 });
    }
  };

  const handleIframeError = () => {
    console.error('ProductPage: Iframe failed to load');
    setError('Failed to load product page content');
    setLoading({ isLoading: false, step: '', progress: 0 });
  };

  const checkIframeForErrors = () => {
    try {
      if (iframeRef.current) {
        const iframeDoc = iframeRef.current.contentDocument;
        if (iframeDoc) {
          // Check if the iframe has actual content beyond just the HTML structure
          const body = iframeDoc.body;
          if (body && body.children.length > 0) {
            // Check if there are any interactive elements or content
            const hasContent = body.innerHTML.length > 100 || body.querySelector('button, input, form, .app, #app');
            if (hasContent) {
              console.log('ProductPage: Iframe content verified as loaded with interactive elements');
              handleIframeLoad();
              return true;
            } else {
              console.log('ProductPage: Iframe loaded but appears to be empty or static');
              // If content appears to be static/empty, wait a bit longer
              return false;
            }
          }
        }
      }
    } catch (e) {
      // Cross-origin error, which is expected for IPFS content
      console.log('ProductPage: Cross-origin iframe, checking for content via postMessage');
      // Try to communicate with the iframe to check if it's ready
      try {
        iframeRef.current?.contentWindow?.postMessage({ type: 'PING' }, '*');
      } catch (postError) {
        console.log('ProductPage: PostMessage failed, assuming loaded');
        handleIframeLoad();
        return true;
      }
      return false;
    }
    return false;
  };

  const handleIframeLoad = () => {
    console.log('ProductPage: Iframe loaded successfully');
    setLoading({ isLoading: false, step: '', progress: 100 });
    setError(null);
    
    // Set up message listener for iframe communication
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      
      console.log('ProductPage: Received message from iframe:', event.data);
      
      if (event.data.type === 'PONG') {
        console.log('ProductPage: Iframe is responsive');
        handleIframeLoad();
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Clean up listener after a short delay
    setTimeout(() => {
      window.removeEventListener('message', handleMessage);
    }, 5000);
  };

  const checkIframeContent = () => {
    // Check iframe content after a delay
    setTimeout(() => {
      if (!checkIframeForErrors()) {
        // If still not loaded, try again
        setTimeout(() => {
          if (!checkIframeForErrors()) {
            console.log('ProductPage: Iframe content check failed, completing load anyway');
            handleIframeLoad();
          }
        }, 2000);
      }
    }, 1000);
  };

  const handleRetry = () => {
    setError(null);
    if (pageCIDs[currentPageIndex]) {
      const ipfsUrl = processUIPageData(pageCIDs[currentPageIndex]);
      setIframeSrc(ipfsUrl);
      setLoading({ isLoading: true, step: 'Loading IPFS content...', progress: 95 });
      
      // Set a timeout to complete loading after 10 seconds
      loadingTimeoutRef.current = setTimeout(() => {
        console.log('ProductPage: Loading timeout reached, completing load');
        setLoading({ isLoading: false, step: '', progress: 100 });
      }, 10000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Loading Bar */}
      {loading.isLoading && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-800 border-b border-gray-700">
          <div className="max-w-4xl mx-auto p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-300">{loading.step}</span>
              <span className="text-sm text-gray-400">{loading.progress}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${loading.progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Product Header */}
      {(() => {
        console.log('ProductPage: Rendering product header with:', { productImage, productName, address });
        return (productImage || productName) && (
          <div className="max-w-4xl mx-auto p-6" style={{ marginTop: loading.isLoading ? '80px' : '0' }}>
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-6">
                {productImage && typeof productImage === 'string' && productImage.trim() !== '' && (
                  <div className="flex-shrink-0" ref={(el) => {
                    if (el) {
                      console.log('ProductPage: Image container dimensions:', {
                        offsetWidth: el.offsetWidth,
                        offsetHeight: el.offsetHeight,
                        clientWidth: el.clientWidth,
                        clientHeight: el.clientHeight,
                        style: el.style.cssText,
                        display: window.getComputedStyle(el).display,
                        visibility: window.getComputedStyle(el).visibility,
                        position: window.getComputedStyle(el).position
                      });
                    }
                  }}>
                    <img 
                      src={currentImageUrl}
                      alt={productName || 'Product'}
                      className="w-24 h-24 rounded-lg object-cover border border-gray-600"
                      onError={handleImageError}
                      onLoad={(e) => {
                        console.log('ProductPage: Successfully loaded product image:', productImage);
                        console.log('ProductPage: Image URL that succeeded:', currentImageUrl);
                        console.log('ProductPage: Image element dimensions:', {
                          width: e.currentTarget.width,
                          height: e.currentTarget.height,
                          offsetWidth: e.currentTarget.offsetWidth,
                          offsetHeight: e.currentTarget.offsetHeight,
                          style: e.currentTarget.style.cssText,
                          display: window.getComputedStyle(e.currentTarget).display,
                          visibility: window.getComputedStyle(e.currentTarget).visibility
                        });
                      }}
                      style={{ minWidth: '96px', minHeight: '96px' }}
                    />
                  </div>
                )}
                <div className="flex-1">
                  {productName && typeof productName === 'string' && productName.trim() !== '' && (
                    <h1 className="text-2xl font-bold text-white mb-2">{productName}</h1>
                  )}
                  {address && (
                    <p className="text-sm text-gray-400 font-mono">{address}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pagination Links */}
      <div className="flex justify-center gap-2 py-4" style={{ marginTop: loading.isLoading ? '80px' : '0' }}>
        {Array.from({ length: pageCount }, (_, i) => (
          <button
            key={i}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${i === currentPageIndex ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-blue-600 hover:text-white'}`}
            onClick={() => handlePageClick(i)}
            disabled={loading.isLoading || i === currentPageIndex}
          >
            {i + 1}
          </button>
        ))}
      </div>
      
      {loading.isLoading && loading.progress < 95 ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">{loading.step}</p>
          </div>
        </div>
      ) : iframeSrc ? (
        <div className="relative">
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            className="w-full h-screen border-0"
            title="Product Page"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
          {/* Show loading overlay if still loading at 95% */}
          {loading.isLoading && loading.progress === 95 && (
            <div className="absolute top-4 left-4 right-4 bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-blue-500 text-center font-medium">
                Content may be loading... If this persists, try refreshing or switching pages.
              </p>
            </div>
          )}
          {error && (
            <div className="absolute top-4 left-4 right-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-red-500 text-center font-medium mb-2">{error}</p>
              <button
                onClick={handleRetry}
                className="mx-auto block px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="mt-4 text-gray-400">No product page found.</p>
            {error && (
              <div className="mt-4">
                <div className="text-red-500 mb-2">{error}</div>
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 